import { useEffect, useRef, useState } from "react";
import { useFormikContext } from "formik";
import { v4 as uuidv4 } from "uuid";
import { useLocation } from "wouter";
import { useClusterStore } from "@/store/cluster";
import {
  useContainerStore,
  useKubernetesStore,
  useVirtualMachineStore
} from "@/store/simpleCluster";
import { useUserStore } from "@/store/user";
import { ClusterHardwareInfo, ClusterType, SimpleClusterForm } from "@/types";
import { toFriendlyPublicError } from "@/utils/api";
import { useToast } from "@/components/ui/use-toast";
import ENV_VARIABLES from "@/env";
import { PAYMENT_LOADING_HTML } from "@/constants";
import { VerticalFlowAnalytics } from "../analytics";
import { useTotalSavings } from "./useTotalSavings";
import {
  creditsBalancesSatisfyPayment,
  getCreditsTopUpAmount,
  readCreditsBalances,
  type CreditsTopUpPaymentMethod
} from "../creditsPaymentAmount";
import { usePurchasedCreditsGate } from "./usePurchasedCreditsGate";
import { clearPersistedClusterDraft } from "@/utils/cluster";
import { getPaygSuggestedTopUp, isPaygCluster } from "@/utils/payg";
import { getStripePaymentLinkAmount } from "@/utils/payment";

const defaultCreditsTopUpPaymentMethod = (): CreditsTopUpPaymentMethod =>
  ENV_VARIABLES.IS_FIAT_ENABLED ? "stripe" : "usdc";

type UsePayAndDeployActionProps = {
  /** True when the payment section is visible (replaces `isOpen` from the dialog). */
  isActive: boolean;
  initialTotalCost?: number;
  analytics: Pick<VerticalFlowAnalytics, "trackPaymentButtonClicked" | "trackDeployedSuccessfully">;
  persistedStorageKey: string;
  fetchClusterHardwareInfo: (
    values: Partial<SimpleClusterForm>
  ) => Promise<ClusterHardwareInfo | null>;
};

export const usePayAndDeployAction = ({
  isActive,
  initialTotalCost,
  analytics,
  persistedStorageKey,
  fetchClusterHardwareInfo
}: UsePayAndDeployActionProps) => {
  const [isPriceLoaded, setIsPriceLoaded] = useState(false);
  const { values, setFieldValue } = useFormikContext<SimpleClusterForm>();
  const createVmCluster = useVirtualMachineStore((state) => state.createCluster);
  const createContainerCluster = useContainerStore((state) => state.createCluster);
  const createKubernetesCluster = useKubernetesStore((state) => state.createCluster);
  const fetchClusterBalance = useClusterStore((s) => s.fetchClusterBalance);
  const mockSpherePayment = useClusterStore((s) => s.mockSpherePayment);
  const generatePaymentLink = useUserStore((s) => s.generatePaymentLink);
  const generateStripePaymentLink = useUserStore((s) => s.generateStripePaymentLink);
  const fetchBalances = useUserStore((s) => s.fetchBalances);
  const userId = useUserStore((s) => s.userId);
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCreditsConfirmOpen, setIsCreditsConfirmOpen] = useState(false);
  const [creditsTopUpPaymentMethod, setCreditsTopUpPaymentMethod] =
    useState<CreditsTopUpPaymentMethod>(defaultCreditsTopUpPaymentMethod);
  const creditsTopUpPaymentMethodRef = useRef<CreditsTopUpPaymentMethod>(
    defaultCreditsTopUpPaymentMethod()
  );
  const [paymentLink, setPaymentLink] = useState<string | undefined>();
  const isMountedRef = useRef(true);
  const paymentCancelledRef = useRef(false);
  const priceRequestIdRef = useRef(0);

  const updateCreditsTopUpPaymentMethod = (method: CreditsTopUpPaymentMethod) => {
    creditsTopUpPaymentMethodRef.current = method;
    setCreditsTopUpPaymentMethod(method);
  };

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Reset state when payment section hides
  useEffect(() => {
    if (!isActive) {
      setIsSubmitting(false);
      setIsCreditsConfirmOpen(false);
      paymentCancelledRef.current = false;
      setPaymentLink(undefined);
    }
  }, [isActive]);

  // Load latest price when payment section becomes active / replica or duration changes
  useEffect(() => {
    if (!isActive) return;

    const requestId = ++priceRequestIdRef.current;
    const load = async () => {
      try {
        if (!values.durationQuantity) return;
        const parsedReplicaCount = parseInt(String(values.replicaCount ?? ""), 10);
        if (!Number.isFinite(parsedReplicaCount) || parsedReplicaCount < 1) return;

        setIsPriceLoaded(false);
        const normalizedReplicaCount = String(parsedReplicaCount);
        const clusterHardwarePricing = await fetchClusterHardwareInfo({
          ...values,
          replicaCount: normalizedReplicaCount,
          locations: values.locations?.map((location) => `"${location}"`),
          hardware: values.deploymentId
        });
        if (!isMountedRef.current || requestId !== priceRequestIdRef.current) return;
        if (clusterHardwarePricing) {
          setFieldValue("hardwareInfo", clusterHardwarePricing);
        }
      } catch (error) {
        if (!isMountedRef.current || requestId !== priceRequestIdRef.current) return;
        const errorMessage = toFriendlyPublicError(error);
        toast({
          title: errorMessage.message || "Failed to fetch price, please try again",
          variant: "error"
        });
      } finally {
        if (isMountedRef.current && requestId === priceRequestIdRef.current) {
          setIsPriceLoaded(true);
        }
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    values.durationQuantity,
    values.durationType,
    values.currency,
    values.hardware,
    values.deploymentId,
    values.locations,
    values.replicaCount,
    values.gpusPerNode,
    values.gpusPerContainer,
    values.priceUpdateTrigger,
    fetchClusterHardwareInfo,
    isActive
  ]);

  // These flows always pay from credits; the confirm modal is where a short balance
  // gets topped up (card or USDC), so there is no method left to pick on the page.
  useEffect(() => {
    if (values.currency !== "credits") {
      setFieldValue("currency", "credits");
    }
  }, [values.currency, setFieldValue]);

  // Keep the balance behind the inline summary fresh while the section is open.
  useEffect(() => {
    if (!isActive) return;
    void fetchBalances(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  const clusterType = values.clusterType || "vmaas";

  const totalCost = values.hardwareInfo?.totalCost;

  const { showSavingsMessage, totalSavingsAmountFormatted, totalCostInUsd } = useTotalSavings({
    totalCost,
    initialTotalCost,
    values,
    clusterType: values.clusterType as ClusterType,
    isPriceLoaded,
    isOpen: isActive
  });

  const minNonPromotionalCreditsRequired = values.hardwareInfo?.minNonPromotionalCreditsRequired;
  const purchasedCreditsGate = usePurchasedCreditsGate({
    minNonPromotionalCreditsRequired,
    totalCost: Number(totalCost || initialTotalCost || 0),
    enabled: isActive
  });

  const handlePayAndDeploy = async (confirmedPaymentAmount?: number) => {
    if (isSubmitting) return;
    clearPersistedClusterDraft(persistedStorageKey);
    paymentCancelledRef.current = false;
    setIsSubmitting(true);
    try {
      const startTime = Date.now();
      const amount = Number(confirmedPaymentAmount ?? (totalCost || initialTotalCost || 0));
      const providerFee = Number(values.hardwareInfo?.providerFee || 0);
      const currency = values.currency;
      const stripePaymentAmount = getStripePaymentLinkAmount(amount, providerFee);
      // Stripe credits the pre-fee amount; match ClusterDeployment's grossCost check.
      const confirmationAmount =
        currency === "stripe" ? Number(values.hardwareInfo?.cost ?? stripePaymentAmount) : amount;
      const minNonPromoRequired = Number(
        values.hardwareInfo?.minNonPromotionalCreditsRequired || 0
      );
      analytics.trackPaymentButtonClicked({ currency, amount });

      const openInNewTab = (url: string) => {
        const newWindow = window.open("", "_blank");
        if (newWindow) {
          newWindow.document.body.innerHTML = PAYMENT_LOADING_HTML;
          newWindow.location.href = url;
        } else {
          window.open(url, "_blank");
        }
      };

      let currentResourceId: string | undefined = values.resourceId;

      if (currency === "credits") {
        const balances = await fetchBalances(true);
        const { creditsBalance, purchasedCreditsBalance, courtesyCreditsBalance } =
          readCreditsBalances(balances);
        const topUpAmount = getCreditsTopUpAmount({
          totalCost: amount,
          creditsBalance,
          purchasedCreditsBalance,
          minNonPromotionalCreditsRequired: minNonPromoRequired,
          courtesyCreditsBalance
        });
        if (topUpAmount <= 0) {
          currentResourceId = uuidv4();
          setPaymentLink(undefined);
        } else if (creditsTopUpPaymentMethodRef.current === "stripe") {
          // Card top-up uses Stripe floor ($10 + fee) via getStripePaymentLinkAmount.
          const topUpStripeAmount = getStripePaymentLinkAmount(topUpAmount);
          const { paymentLink: link, resourceId } = await generateStripePaymentLink({
            amount: topUpStripeAmount,
            resourceId: values.resourceId ? values.resourceId : uuidv4()
          });
          currentResourceId = resourceId;
          if (link) {
            setPaymentLink(link);
            openInNewTab(link);
          }
        } else {
          // Shortfall paid via USDC generate-payment-link (top-up-credits has a $10 min).
          // PAYG keeps its own $10 floor on top: the deploy UI quotes that minimum up
          // front, and the max() keeps any non-promotional shortfall still covered.
          const usdcTopUpAmount = isPaygCluster(values.clusterType)
            ? Math.max(topUpAmount, getPaygSuggestedTopUp(amount, creditsBalance))
            : topUpAmount;
          const { paymentLink: link, resourceId } = await generatePaymentLink({
            name: userId,
            amount: usdcTopUpAmount,
            currency: "usdc",
            type: "cluster",
            resourceId: values.resourceId ? values.resourceId : uuidv4()
          });
          currentResourceId = resourceId;
          if (link) {
            setPaymentLink(link);
            openInNewTab(link);
          }
        }
      } else if (currency === "stripe") {
        const { paymentLink: link, resourceId } = await generateStripePaymentLink({
          amount: stripePaymentAmount
        });
        currentResourceId = resourceId;
        setPaymentLink(link);
        openInNewTab(link);
      } else {
        const { paymentLink: link, resourceId } = await generatePaymentLink({
          name: userId,
          amount,
          currency,
          type: "cluster",
          resourceId: values.resourceId
            ? values.resourceId
            : currency === "usdc"
              ? uuidv4()
              : undefined
        });
        currentResourceId = resourceId;
        setPaymentLink(link);
        openInNewTab(link);
      }

      if (!currentResourceId) {
        throw new Error("Unable to initiate payment. Please try again.");
      }
      if (paymentCancelledRef.current) return;
      if (isMountedRef.current) {
        setFieldValue("resourceId", currentResourceId);
      }

      // Poll for payment confirmation.
      // Credits, Stripe, and USDC all settle into user credit balances before deploy.
      // Require both total coverage AND paid-credit floor so we do not call deploy early
      // when total credits already cover cost but paid credits are still short (e.g. promo-heavy).
      let isPaid = false;
      while (!isPaid && isMountedRef.current && !paymentCancelledRef.current) {
        try {
          if (currency === "credits" || currency === "stripe" || currency === "usdc") {
            const balances = await fetchBalances(true);
            const { creditsBalance, purchasedCreditsBalance, courtesyCreditsBalance } =
              readCreditsBalances(balances);
            if (
              amount > 0 &&
              creditsBalancesSatisfyPayment({
                totalCost: amount,
                creditsBalance,
                purchasedCreditsBalance,
                minNonPromotionalCreditsRequired: minNonPromoRequired,
                courtesyCreditsBalance
              })
            ) {
              isPaid = true;
              break;
            }
          } else {
            const balance = await fetchClusterBalance(currentResourceId);
            if (!isMountedRef.current) break;
            if (confirmationAmount > 0 && balance >= confirmationAmount) {
              isPaid = true;
              break;
            }
          }
          if (!isMountedRef.current) break;
        } catch {
          // continue polling
        }
        await new Promise((r) => setTimeout(r, 1500));
      }

      if (paymentCancelledRef.current) return;

      // Deploy cluster
      if (clusterType === "vmaas") {
        await createVmCluster({
          ...values,
          resourceId: currentResourceId,
          hardware: values.deploymentId as string
        });
      } else if (clusterType === "container" || clusterType === "caas") {
        await createContainerCluster({
          ...values,
          resourceId: currentResourceId,
          hardware: values.deploymentId as string
        });
      } else if (clusterType === "kubernetes") {
        await createKubernetesCluster({
          ...values,
          resourceId: currentResourceId,
          hardware: values.deploymentId as string
        });
      }

      analytics.trackDeployedSuccessfully({
        userId,
        values,
        currency,
        amount,
        deploymentTimeInSeconds: (Date.now() - startTime) / 1000,
        status: "submitted"
      });

      const redirectUrl =
        clusterType === "vmaas"
          ? "/cloud/virtual-machines"
          : clusterType === "container" || clusterType === "caas"
            ? "/cloud/container"
            : clusterType === "kubernetes"
              ? "/cloud/kubernetes"
              : "/cloud/clusters";

      if (isMountedRef.current) {
        setLocation(redirectUrl);
      }
    } catch (e) {
      if (paymentCancelledRef.current) return;
      const errorMessage = toFriendlyPublicError(e);
      const message = errorMessage instanceof Error ? errorMessage.message : "Something went wrong";
      if (isMountedRef.current) {
        toast({ title: message, variant: "error" });
      }
    } finally {
      if (isMountedRef.current) {
        setIsSubmitting(false);
      }
    }
  };

  const cancelPayment = () => {
    paymentCancelledRef.current = true;
    setIsSubmitting(false);
    setIsCreditsConfirmOpen(false);
    setPaymentLink(undefined);
  };

  const requestPayAndDeploy = () => {
    if (isSubmitting) return;
    if (values.currency === "credits") {
      updateCreditsTopUpPaymentMethod(defaultCreditsTopUpPaymentMethod());
      setIsCreditsConfirmOpen(true);
      return;
    }
    void handlePayAndDeploy();
  };

  const confirmCreditsPayment = (paymentAmount: number) => {
    setIsCreditsConfirmOpen(false);
    void handlePayAndDeploy(paymentAmount);
  };

  const cancelCreditsPaymentConfirm = () => {
    setIsCreditsConfirmOpen(false);
  };

  return {
    isPriceLoaded,
    totalCost,
    paymentLink,
    isSubmitting,
    showSavingsMessage,
    totalSavingsAmountFormatted,
    totalCostInUsd,
    handlePayAndDeploy,
    requestPayAndDeploy,
    isCreditsConfirmOpen,
    creditsTopUpPaymentMethod,
    setCreditsTopUpPaymentMethod: updateCreditsTopUpPaymentMethod,
    confirmCreditsPayment,
    cancelCreditsPaymentConfirm,
    cancelPayment,
    mockSpherePayment,
    purchasedCreditsGate,
    minNonPromotionalCreditsRequired: Number(minNonPromotionalCreditsRequired || 0)
  };
};
